/**
 * AddSectorDialog.jsx - Dialog pour ajouter un nouveau secteur
 *
 * Permet de saisir:
 * - ID du secteur (sector_id) - obligatoire
 * - Nom (optionnel)
 * - Compartiment (optionnel)
 * - Localisation (GPS, clic carte, saisie manuelle)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Close, MyLocation, Hexagon } from '@mui/icons-material';
import FarmMapService from './FarmMapService';

/**
 * Dialog pour ajouter un secteur
 */
const AddSectorDialog = ({
  open,
  onClose,
  farmId,
  compartments = [],
  onSectorAdded,
  initialLat = null,
  initialLng = null
}) => {
  // État du formulaire
  const [formData, setFormData] = useState({
    sector_id: '',
    name: '',
    compartment_id: '',
    gps_latitude: initialLat || '',
    gps_longitude: initialLng || ''
  });

  // État de l'interface
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Réinitialiser le formulaire quand le dialog s'ouvre
  useEffect(() => {
    if (open) {
      setFormData({
        sector_id: '',
        name: '',
        compartment_id: '',
        gps_latitude: initialLat || '',
        gps_longitude: initialLng || ''
      });
      setError('');
    }
  }, [open, initialLat, initialLng]);

  /**
   * Gérer les changements de champ
   */
  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError('');
  };

  /**
   * Obtenir la position GPS de l'utilisateur
   */
  const handleGetLocation = async () => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée par ce navigateur');
      return;
    }

    setIsGettingLocation(true);
    setError('');

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      setFormData(prev => ({
        ...prev,
        gps_latitude: position.coords.latitude.toFixed(6),
        gps_longitude: position.coords.longitude.toFixed(6)
      }));
    } catch (err) {
      setError(`Impossible d'obtenir la position: ${err.message}`);
    } finally {
      setIsGettingLocation(false);
    }
  };

  /**
   * Valider le formulaire
   */
  const validateForm = () => {
    if (!formData.sector_id.trim()) {
      setError("L'ID du secteur est obligatoire");
      return false;
    }

    // Validation format ID (alphanumérique, 2-50 caractères)
    const idRegex = /^[a-zA-Z0-9\-_]{2,50}$/;
    if (!idRegex.test(formData.sector_id.trim())) {
      setError('ID de secteur invalide (2-50 caractères alphanumériques, tirets, underscores)');
      return false;
    }

    // Validation coordonnées si fournies
    if (formData.gps_latitude || formData.gps_longitude) {
      const lat = parseFloat(formData.gps_latitude);
      const lng = parseFloat(formData.gps_longitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setError('Latitude invalide (-90 à 90)');
        return false;
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setError('Longitude invalide (-180 à 180)');
        return false;
      }
    }

    return true;
  };

  /**
   * Soumettre le formulaire
   */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // FarmMapService.createSector attend camelCase et fait la conversion vers snake_case
      const sectorData = {
        sectorId: formData.sector_id.trim(),
        farmId: farmId,
        name: formData.name.trim() || undefined,
        compartmentId: formData.compartment_id || undefined,
        gpsLatitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : undefined,
        gpsLongitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : undefined,
        metadata: {
          created_at: new Date().toISOString(),
          created_via: 'map_interface'
        }
      };

      const createdSector = await FarmMapService.createSector(sectorData);

      if (onSectorAdded) {
        onSectorAdded(createdSector);
      }

      onClose();
    } catch (err) {
      console.error('Erreur création secteur:', err);
      setError(err.message || 'Erreur lors de la création du secteur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3000 }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Nouveau secteur</Typography>
          <IconButton onClick={onClose} disabled={loading} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* ID du secteur */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ID du secteur *"
              value={formData.sector_id}
              onChange={handleChange('sector_id')}
              placeholder="Ex: serre-1-ouest"
              disabled={loading}
              helperText="Identifiant unique (2-50 caractères alphanumériques)"
              required
            />
          </Grid>

          {/* Nom */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nom (optionnel)"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="Ex: Serre 1 - Ouest"
              disabled={loading}
            />
          </Grid>

          {/* Compartiment */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="compartment-select-label">Compartiment (optionnel)</InputLabel>
              <Select
                labelId="compartment-select-label"
                value={formData.compartment_id}
                label="Compartiment (optionnel)"
                onChange={handleChange('compartment_id')}
                disabled={loading || compartments.length === 0}
              >
                <MenuItem value="">
                  <em>Aucun</em>
                </MenuItem>
                {compartments.map((c) => (
                  <MenuItem key={c.compartment_id || c.id || c.code} value={c.compartment_id || c.id || c.code}>
                    {c.name || c.label || c.code || c.compartment_id}
                  </MenuItem>
                ))}
              </Select>
              {compartments.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Aucun compartiment disponible
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Coordonnées GPS */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Localisation initiale (optionnel)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MyLocation />}
                onClick={handleGetLocation}
                disabled={loading || isGettingLocation}
              >
                {isGettingLocation ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Obtention...
                  </>
                ) : (
                  'Utiliser ma position'
                )}
              </Button>
              <Typography variant="caption" color="text.secondary">
                ou saisir manuellement
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Latitude"
              value={formData.gps_latitude}
              onChange={handleChange('gps_latitude')}
              placeholder="Ex: 35.088056"
              disabled={loading}
              type="number"
              inputProps={{ step: '0.000001' }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Longitude"
              value={formData.gps_longitude}
              onChange={handleChange('gps_longitude')}
              placeholder="Ex: -6.108250"
              disabled={loading}
              type="number"
              inputProps={{ step: '0.000001' }}
            />
          </Grid>

          {/* Aide */}
          <Grid item xs={12}>
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 Vous pouvez créer le secteur avec un simple point central, puis tracer la délimitation polygonale depuis la carte.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.sector_id.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <Hexagon />}
        >
          {loading ? 'Création...' : 'Créer le secteur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSectorDialog;
